import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import { CircleNotch, ArrowLeft, FloppyDisk, CheckCircle, Plus, Trash, Warning } from "@phosphor-icons/react";
import CoPilotPanel, { CoPilotToggle } from "@/components/CoPilotPanel";
import { RACI_MARKERS, MARKER_LABEL, MARKER_CLS, stepAccountabilityIssue } from "@/components/blueprints/RaciEBlueprint";

const rid = () => "rl_" + Math.random().toString(36).slice(2, 10);
const sid = () => "st_" + Math.random().toString(36).slice(2, 10);
const inputCls = "flex h-10 w-full rounded-sm border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500";

export default function RaciE() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState("");
  const [context, setContext] = useState("");
  const [roles, setRoles] = useState([]);
  const [steps, setSteps] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try { const res = await api.get(`/documents/${id}`); const pl = res.data.payload || {}; setTitle(res.data.title || ""); setContext(pl.context || ""); setRoles(pl.roles || []); setSteps(pl.steps || []); }
      catch { toast.error("Document not found"); navigate("/dashboard"); }
      finally { setLoading(false); }
    })();
  }, [id]); // eslint-disable-line

  const persist = useCallback(async (extra = {}) => {
    setSaving(true);
    try { await api.put(`/documents/${id}`, { title: title || "Untitled RACI-E Grid", payload: { context, roles, steps }, ...extra }); return true; }
    catch { toast.error("Save failed"); return false; }
    finally { setSaving(false); }
  }, [id, title, context, roles, steps]);

  const checkGovernance = () => {
    const named = steps.filter((s) => s.name?.trim());
    const issues = named.map((s) => ({ s, issue: stepAccountabilityIssue(s) })).filter((x) => x.issue);
    issues.forEach(({ s, issue }) => toast.warning(`${s.name}: ${issue.msg}`));
    return issues.length;
  };

  const addRole = () => setRoles((x) => [...x, { id: rid(), name: "" }]);
  const setRoleName = (i, name) => setRoles((x) => x.map((r, j) => (j === i ? { ...r, name } : r)));
  const removeRole = (i) => {
    const role = roles[i];
    setRoles((x) => x.filter((_, j) => j !== i));
    setSteps((x) => x.map((s) => { const a = { ...(s.assignments || {}) }; delete a[role.id]; return { ...s, assignments: a }; }));
  };
  const addStep = () => setSteps((x) => [...x, { id: sid(), name: "", assignments: {} }]);
  const setStepName = (i, name) => setSteps((x) => x.map((s, j) => (j === i ? { ...s, name } : s)));
  const setAssignment = (i, roleId, marker) => setSteps((x) => x.map((s, j) => (j === i ? { ...s, assignments: { ...(s.assignments || {}), [roleId]: marker } } : s)));
  const removeStep = (i) => setSteps((x) => x.filter((_, j) => j !== i));

  const saveDraft = async () => { if (await persist()) { checkGovernance(); toast.success("Draft saved"); } };
  const finalise = async () => { if (await persist({ status: "Completed" })) { checkGovernance(); toast.success("RACI-E grid finalised"); navigate(`/raci/${id}/view`); } };

  if (loading) return <Layout><div className="flex items-center justify-center py-40 text-slate-400"><CircleNotch size={28} className="animate-spin" /></div></Layout>;

  const activeRoles = roles.filter((r) => r.name?.trim());

  return (
    <Layout>
      <div className="max-w-6xl mx-auto w-full py-8 px-6 lg:px-10">
        <button data-testid="back-link" onClick={() => navigate("/dashboard")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-3"><ArrowLeft size={16} /> Workspace</button>
        <input data-testid="racie-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="RACI-E grid title…" className="w-full bg-transparent text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 placeholder:text-slate-300 focus:outline-none border-b border-transparent focus:border-slate-300 pb-1" />
        <textarea data-testid="racie-context-input" rows={2} value={context} onChange={(e) => setContext(e.target.value)} placeholder="Context — project or process being mapped…" className="mt-3 w-full rounded-sm border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500" />

        {/* Roles */}
        <div className="mt-6 bg-white border border-slate-200 rounded-sm shadow-sm p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div><h2 className="text-xl font-semibold text-slate-900">Roles</h2><p className="text-sm text-slate-500">The people or functions across the top of the grid.</p></div>
            <button data-testid="racie-add-role" onClick={addRole} className="inline-flex items-center gap-1.5 rounded-sm border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"><Plus size={16} /> Add role</button>
          </div>
          {roles.length === 0 && <p className="text-sm text-slate-400 border border-dashed border-slate-300 rounded-sm p-4 text-center">Add roles, then map them against each step below.</p>}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {roles.map((r, i) => (
              <div key={r.id} className="flex items-center gap-2">
                <input data-testid={`racie-role-${i}`} className={inputCls} value={r.name} onChange={(e) => setRoleName(i, e.target.value)} placeholder={`Role ${i + 1}`} />
                <button data-testid={`racie-role-remove-${i}`} onClick={() => removeRole(i)} className="p-2 text-slate-400 hover:text-red-600"><Trash size={16} /></button>
              </div>
            ))}
          </div>
        </div>

        {/* Matrix */}
        <div className="mt-6 bg-white border border-slate-200 rounded-sm shadow-sm p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div><h2 className="text-xl font-semibold text-slate-900">Accountability Matrix</h2><p className="text-sm text-slate-500">Assign R / A / C / I and the E (Enabler / Executor) for each step. Exactly one A per step.</p></div>
            <button data-testid="racie-add-step" onClick={addStep} className="inline-flex items-center gap-1.5 rounded-sm border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"><Plus size={16} /> Add step</button>
          </div>
          {steps.length === 0 ? (
            <p className="text-sm text-slate-400 border border-dashed border-slate-300 rounded-sm p-4 text-center">Add project steps to begin mapping.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm min-w-[640px]">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                    <th className="px-3 py-2.5 font-semibold sticky left-0 bg-slate-50 min-w-[200px]">Step / Activity</th>
                    {activeRoles.map((r) => <th key={r.id} className="px-2 py-2.5 font-semibold text-center whitespace-nowrap">{r.name}</th>)}
                    <th className="px-2 py-2.5" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {steps.map((s, i) => {
                    const issue = stepAccountabilityIssue(s);
                    return (
                      <tr key={s.id} data-testid={`racie-step-row-${i}`} className={issue ? "bg-amber-50" : ""}>
                        <td className="px-3 py-2 sticky left-0 bg-inherit">
                          <input data-testid={`racie-step-name-${i}`} className={inputCls} value={s.name} onChange={(e) => setStepName(i, e.target.value)} placeholder={`Step ${i + 1}`} />
                          {issue && <span data-testid={`racie-inline-flag-${i}`} className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase text-amber-700"><Warning size={11} weight="fill" /> {issue.msg}</span>}
                        </td>
                        {activeRoles.map((r) => {
                          const m = (s.assignments || {})[r.id] || "";
                          return (
                            <td key={r.id} className="px-2 py-2 text-center">
                              <select data-testid={`racie-cell-${i}-${r.id}`} value={m} onChange={(e) => setAssignment(i, r.id, e.target.value)}
                                className={`h-9 rounded-sm border border-slate-300 px-1.5 text-xs font-mono font-bold focus:outline-none focus:ring-2 focus:ring-emerald-500 ${m ? MARKER_CLS[m] : "bg-white text-slate-400"}`}>
                                {RACI_MARKERS.map((mk) => <option key={mk || "none"} value={mk}>{mk || "—"}</option>)}
                              </select>
                            </td>
                          );
                        })}
                        <td className="px-2 py-2 text-center"><button data-testid={`racie-step-remove-${i}`} onClick={() => removeStep(i)} className="p-2 text-slate-400 hover:text-red-600"><Trash size={16} /></button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 font-mono text-[11px] text-slate-500 pt-1">
            {Object.entries(MARKER_LABEL).map(([k, label]) => (
              <span key={k} className="inline-flex items-center gap-1.5"><span className={`inline-flex w-5 h-5 items-center justify-center rounded-sm text-[10px] font-bold ${MARKER_CLS[k]}`}>{k}</span>{label}</span>
            ))}
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end gap-3 sticky bottom-0 bg-slate-50 py-4">
          <button data-testid="racie-save-draft" onClick={saveDraft} disabled={saving} className="inline-flex items-center gap-2 rounded-sm border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50">{saving ? <CircleNotch size={16} className="animate-spin" /> : <FloppyDisk size={16} />} Save Draft</button>
          <button data-testid="racie-finalise" onClick={finalise} disabled={saving} className="inline-flex items-center gap-2 rounded-sm bg-emerald-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-emerald-700"><CheckCircle size={18} weight="fill" /> Finalise Grid</button>
        </div>
      </div>
      <CoPilotToggle open={open} onClick={() => setOpen(true)} phase="PEOPLE" />
      <CoPilotPanel open={open} onClose={() => setOpen(false)} docId={id} toolType="raci-e-grid" step={{ label: "RACI-E Grid" }} phase="PEOPLE" onApplySuggestion={(t) => { navigator.clipboard?.writeText(t); toast.info("Copied to clipboard"); }} />
    </Layout>
  );
}
